import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@swyftx/aviary/atoms/Icon';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Body, Numeric } from '@swyftx/aviary/atoms/Typography';
import { FundsAdjustment } from '@swyftx/aviary/icons/outlined';
import { EnhancedTableData, EnhancedTableHeaderData, EnhancedTableSort } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginTransaction } from '@swyftx/margin/build/types';

import { formatCurrency } from '@shared/utils';

import { capitalize } from '@utils/formatting';

import { DateTime } from 'luxon';
import { getGroupByTitle } from 'src/lib/dynamic-price-alerts/utils/dynamic-price-alerts.utils';
import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginTransactions } from 'src/lib/margin/hooks/transactions';

import { MarginBalanceAdjustmentsTableData } from '../MarginBalanceAdjustmentsTable.data';

type MarginPositionsHeaderData = { [key in keyof MarginBalanceAdjustmentsTableData]: EnhancedTableHeaderData };

type Props = {
  sortKey?: keyof MarginBalanceAdjustmentsTableData;
  isDesktopView?: boolean;
  isSm?: boolean;
  isXs?: boolean;
};

const useMarginBalanceAdjustmentsTable = ({ sortKey = 'date', isDesktopView, isXs }: Props) => {
  const [sort, setSort] = useState<EnhancedTableSort<MarginBalanceAdjustmentsTableData>>({
    sortKey,
    sortDirection: 'DESC',
  });

  const { t } = useTranslation('margin');
  const { currency } = useMarginAccount();
  const { transactions, isFetching, isFetchingNextPage, isLoading, hasNextPage, isError, fetchNextPage } =
    useMarginTransactions({ sortByDesc: sort.sortDirection === 'DESC', type: ['ADJUSTMENT'] });

  const headers: MarginPositionsHeaderData = useMemo(
    () => ({
      type: {
        title: t('tables.balanceAdjustments.headers.type'),
        alignment: 'start',
        sortable: false,
        enabled: isDesktopView,
      },
      adjustmentDate: {
        title: t('tables.balanceAdjustments.headers.adjustmentDate'),
        alignment: 'start',
        sortable: false,
        enabled: !isDesktopView,
      },
      details: {
        title: t('tables.balanceAdjustments.headers.details'),
        alignment: 'start',
        sortable: false,
        enabled: !isXs,
      },
      amount: {
        title: t('tables.balanceAdjustments.headers.amount'),
        alignment: 'end',
        sortable: false,
        enabled: true,
      },
      date: {
        title: t('tables.balanceAdjustments.headers.date'),
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },
      adjustmentId: {
        title: t('tables.balanceAdjustments.headers.adjustmentId'),
        alignment: 'end',
        sortable: false,
        enabled: isDesktopView,
      },
    }),
    [isDesktopView, isXs, t],
  );

  const data: EnhancedTableData<MarginBalanceAdjustmentsTableData, MarginTransaction>[] = useMemo(
    () =>
      transactions?.map((transaction: MarginTransaction) => ({
        value: transaction,
        type: {
          value: transaction.type,
          element: (
            <FlexLayout spacing={8} alignItems='center'>
              <Body color='primary' size='small'>
                {capitalize(transaction.type)}
              </Body>
            </FlexLayout>
          ),
        },
        adjustmentDate: {
          value: transaction.timestamp,
          groupByTitle: getGroupByTitle(DateTime.fromMillis(transaction.timestamp || 0)),
          element: (
            <FlexLayout spacing={12} alignItems='center'>
              <Icon color='info' icon={<FundsAdjustment />} />
              <FlexLayout direction='column' spacing={0}>
                <Body color='primary' size='small'>
                  {capitalize(transaction.type)}
                </Body>
                <Body color='primary' size='small' className='whitespace-nowrap'>
                  {DateTime.fromMillis(transaction.timestamp).toFormat('hh:mm:ss a')}
                </Body>
              </FlexLayout>
            </FlexLayout>
          ),
        },
        details: {
          value: transaction.reference,
          element: (
            <FlexLayout spacing={8} alignItems='center'>
              <Body color='primary' size='small'>
                {transaction.reference || t('balanceAdjustment.labels.balanceAdjustment')}
              </Body>
            </FlexLayout>
          ),
        },
        amount: {
          value: transaction.amount,
          element: (
            <FlexLayout spacing={8} alignItems='center' justifyContent='end'>
              <Numeric color='primary' size='small' className='whitespace-nowrap'>
                {formatCurrency(transaction.amount, currency, { appendCode: true, hideCode: false })}
              </Numeric>
            </FlexLayout>
          ),
        },
        date: {
          value: transaction.timestamp,
          element: (
            <FlexLayout spacing={8} alignItems='center' justifyContent='end'>
              <Body color='primary' size='small' className='whitespace-nowrap'>
                {DateTime.fromMillis(transaction.timestamp).toFormat('dd/MM/yyyy hh:mm:ss a')}
              </Body>
            </FlexLayout>
          ),
        },
        adjustmentId: {
          value: transaction.transactionId,
          element: (
            <FlexLayout spacing={8} alignItems='center' justifyContent='end'>
              <Body color='primary' size='small' className='whitespace-nowrap'>
                {transaction.transactionId}
              </Body>
            </FlexLayout>
          ),
        },
      })),
    [currency, transactions],
  );

  const onSort = useCallback(
    (newSort?: EnhancedTableSort<MarginBalanceAdjustmentsTableData>) => {
      const tableData: EnhancedTableData<MarginBalanceAdjustmentsTableData, MarginTransaction>[] = Object.assign(
        [],
        data,
      );

      if (!newSort) return tableData;

      setSort(newSort);
    },
    [data],
  );

  return {
    initialSort: sort,
    headers,
    data,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoading,
    hasNextPage,
    isError,
    onSort,
  };
};

export { useMarginBalanceAdjustmentsTable };
